import { Route, Redirect, useLocation } from "react-router-dom"
import {
  isLogin,
  isAccountEncrypted,
  checkAccountDoneSyncEncrypted,
} from "config/environments"
import { PATH } from "constants/path"
import { useAppSelector } from "hooks/useApp"

const PrivateRoute = ({ component: Component, ...rest }) => {
  const location = useLocation()
  const { pathname, search } = location
  const userInfo = useAppSelector((state) => state.userInfo)
  if (!isLogin()) {
    return (
      <Route
        {...rest}
        render={() => (
          <Redirect
            to={{
              pathname: PATH.login,
              state: { redirectUrl: pathname, search },
            }}
          />
        )}
      />
    )
  }

  // Check if user has last_organization_id
  // Only check if userInfo has been loaded (has id)
  // If not, only allow access to specific routes
  const isUserInfoLoaded = !!userInfo?.id
  const hasLastOrganizationId = !!userInfo?.last_organization_id

  // Routes allowed when user doesn't have last_organization_id
  const allowedRoutesWithoutOrg = [
    PATH.notWorkspace,
    PATH.organizationAdd,
    PATH.general,
    PATH.updatePhoneNumberAndSecurityCode,
    PATH.enableEncryption,
    PATH.projectSensitiveData,
    PATH.conversations,
    PATH.inviteesPCB,
    PATH.inviteesBOM,
    PATH.inviteesMechComponent,
    PATH.inviteesOther,
    PATH.inviteesBuildPCB,
    PATH.inviteesBuildBOM,
    PATH.inviteesBuildMechComponent,
    PATH.inviteesBuildOther,
  ]

  // Check if current path is in allowed routes
  // For routes with parameters (like /invitees/component/:id/...), check if pathname starts with the base path
  const isAllowedRoute = allowedRoutesWithoutOrg.some((route) => {
    if (pathname === route) {
      return true
    }
    // For invitee routes with parameters, check if pathname starts with /invitees/component
    if (route.startsWith("/invitees/component")) {
      return pathname.startsWith("/invitees/component")
    }
    // For other routes, check if pathname includes the route
    return pathname.indexOf(route) > -1
  })

  // force user enable encrytion
  const isEnableEncryptionPath = pathname.indexOf(PATH.enableEncryption) > -1
  const accountEncrypted = isAccountEncrypted()
  if (!accountEncrypted && !isEnableEncryptionPath) {
    return (
      <Route
        {...rest}
        render={() => (
          <Redirect
            to={{
              pathname: PATH.enableEncryption,
              state: { redirectUrl: pathname, search },
            }}
          />
        )}
      />
    )
  }
  // force user sync un-encrypted data
  const isEnableSyncPath = pathname.indexOf(PATH.projectSensitiveData) > -1
  if (accountEncrypted) {
    const isDoneSyncEncrypted = checkAccountDoneSyncEncrypted()
    if (!isDoneSyncEncrypted && !isEnableSyncPath) {
      return (
        <Route
          {...rest}
          render={() => (
            <Redirect to={{ pathname: PATH.projectSensitiveData }} />
          )}
        />
      )
    }
  }
  // after enable encryption user can not go to this route again
  if (isEnableEncryptionPath && accountEncrypted) {
    return (
      <Route
        {...rest}
        render={() => <Redirect to={{ pathname: PATH.projects }} />}
      />
    )
  }
  // Only check last_organization_id if userInfo has been loaded
  // If user doesn't have last_organization_id and trying to access restricted route
  if (isUserInfoLoaded && !hasLastOrganizationId && !isAllowedRoute) {
    return (
      <Route
        {...rest}
        render={() => (
          <Redirect
            to={{
              pathname: PATH.notWorkspace,
              state: { redirectUrl: pathname, search },
            }}
          />
        )}
      />
    )
  }
  return <Route {...rest} render={() => Component} />
}

export default PrivateRoute
